import clsx from 'clsx';
import React, { Children, FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { Stack, StackProps } from '../stack';
import classes from './separated-list.module.scss';

export type SeperatedListProps = PropsWithChildren
  & StackProps & {
  /**
   * Represents the separator element to be rendered in a React component.
   *
   * This property accepts a ReactNode, which can be used to customize the separator appearance within the component.
   * If provided, the component will render the specified separator element between its contents.
   */
    separator?: ReactNode;
  };

/**
 * A custom component that renders a list of children elements with an optional separator between them.
 * The children elements are displayed horizontally in a row.
 *
 */
export const SeparatedList: FC<SeperatedListProps> = ({
  children,
  separator,
  ...props
}) => {
  const listOfChildren = useMemo(() => Children.toArray(children), [children]);
  return (
    <Stack
      direction="row"
      crossAxis={separator ? 'center' : 'stretch'}
      align="stretch"
      {...props}
      className={clsx(classes.root, 'separated-list-component', props.className)}
    >
      {Children.map(listOfChildren, (child, index) => {
        return (
          <>
            {index !== 0 && (separator ?? <div className={classes.divider} />)}
            <div className={classes.item}>{child}</div>
          </>
        );
      })}
    </Stack>
  );
};
